import { Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DataGrid, gridClasses, GridColDef, GridFooterContainer, GridPagination } from '@mui/x-data-grid';
import b64toBlob from 'b64-to-blob';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

import Authorization from '../../../utils/Authorization';
import { configureColumnComparisonChart } from '../../ChartConfiguration/ChartConfiguration';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchTransitionalCarePerformance, exportTransitionalCarePerformance, MenuProps } from '../WidgetService';
import { TransitionalCareDateRangeEnum } from '../../../Enum/TransitionalCareDateRangeEnum';
import TeamDropdown from '../../Shared/TeamDropdown';
import { isDefaultDate } from '../../../utils/common';
import { dateFormatter } from '../../../utils/timeFormat';
import { parentUrl } from '../../../utils/constants';
import WidgetLoading from '../../SmartDashboard/Shared/WidgetLoading';
import TransitionalCarePerformanceSummary from '../../../Models/Widget/TransitionalCarePerformanceSummary.model';
import TransitionalCarePerformancePreview from '../../../Models/Widget/TransitionalCarePerformancePreview.model';
import { TCPerformanceFilterRoleEnum } from '../../../Enum/TCPerformanceFilterRoleEnum';
import '../../Widgets/Widgets.css';

const TransitionalCarePerformanceDetail = () => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const chartHeight = 380;
    const auth = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const [tcSummary, setTcSummary] = useState<TransitionalCarePerformanceSummary>(new TransitionalCarePerformanceSummary());
    const [filteredTcSummary, setFilteredTcSummary] = useState<TransitionalCarePerformancePreview[]>([]);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    const [tcCompletedWindow, setTcCompletedWindow] = useState('Seen1To7');
    const [tcIncompleteWindow, setTcIncompleteWindow] = useState('NotSeen1To7');
    const [chartOptions, setChartOptions] = useState({});
    const hasPermission = auth.isInPermission('View TC Follow-Up Average');

    const {
        tcPerformanceTeam,
        setTcPerformanceTeam,
        tcPerformanceFilterRole,
        setTcPerformanceFilterRole,
        tcPerformanceDateFilter,
        setTcPerformanceDateFilter,
        tcPerformanceFromDate,
        setTcPerformanceFromDate,
        tcPerformanceToDate,
        setTcPerformanceToDate,
        tcPerformanceShowInActiveTeams,
        setTcPerformanceShowInActiveTeams,
        tcPerformanceTab,
        setTcPerformanceTab,
    } = useWidgetContext();

    let isClinicalLiaison = tcPerformanceFilterRole === TCPerformanceFilterRoleEnum.ClinicalLiaison;
    useEffect(() => {
        isClinicalLiaison = tcPerformanceFilterRole === TCPerformanceFilterRoleEnum.ClinicalLiaison;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tcPerformanceFilterRole]);

    const completedWindowOptions = isClinicalLiaison
        ? [
            {
                value: "Within24hr",
                label: "Within 24 hr",
                window: "Within 24 hr",
            },
            {
                value: "Within48hr",
                label: "Within 48 hr",
                window: "Within 48 hr",
            },
            {
                value: "Within72hr",
                label: "Within 72 hr",
                window: "Within 72 hr",
            },
            {
                value: "WithinGreaterThan72hr",
                label: ">72 hr",
                window: ">72 hr",
            },
        ]
        : [
            {
                value: "Seen1To7",
                label: "Seen 1-7 Days",
                window: "1-7 Days",
            },
            {
                value: "Seen8To14",
                label: "Seen 8-14 Days",
                window: "8-14 Days",
            },
            {
                value: "Seen15To30",
                label: "Seen 15-30 Days",
                window: "15-30 Days",
            },
            {
                value: "SeenGreaterThan30",
                label: "Seen >30 Days",
                window: ">30 Days",
            },
        ];

    const incompleteWindowOptions = isClinicalLiaison
        ? [
            {
                value: "NotWithin24hr",
                label: "Not Seen in 24 hr",
                window: "Within 24 hr",
            },
            {
                value: "NotWithin48hr",
                label: "Not Seen in 48 hr",
                window: "Within 48 hr",
            },
            {
                value: "NotWithin72hr",
                label: "Not Seen in 72 hr",
                window: "Within 72 hr",
            },
            {
                value: "NotSeenGreaterThan72hr",
                label: "Not Seen >72 hr",
                window: ">72 hr",
            },
        ]
        : [
            {
                value: "NotSeen1To7",
                label: "Not Seen 1-7 Days",
                window: "1-7 Days",
            },
            {
                value: "NotSeen8To14",
                label: "Not Seen 8-14 Days",
                window: "8-14 Days",
            },
            {
                value: "NotSeen15To30",
                label: "Not Seen 15-30 Days",
                window: "15-30 Days",
            },
            {
                value: "NotSeenGreaterThan30",
                label: "Not Seen >30 Days",
                window: ">30 Days",
            },
        ];

    const getCompletedSummary = (window: string) => {
        return tcSummary?.CompletedSummary?.find((s) => s.Window === window);
    };

    const getIncompleteSummary = (window: string) => {
        return tcSummary?.IncompleteSummary?.find((s) => s.Window === window);
    };

    const dateFilterList = [
        { Id: TransitionalCareDateRangeEnum.Last1Month, Value: TransitionalCareDateRangeEnum.Last1MonthDescription },
        { Id: TransitionalCareDateRangeEnum.Last3Months, Value: TransitionalCareDateRangeEnum.Last3MonthsDescription },
        { Id: TransitionalCareDateRangeEnum.Last6Months, Value: TransitionalCareDateRangeEnum.Last6MonthsDescription },
        { Id: TransitionalCareDateRangeEnum.Custom, Value: TransitionalCareDateRangeEnum.CustomDescription },
    ];

    const followUpRules = [
        { label: TCPerformanceFilterRoleEnum.LicensedStaffDescription, value: TCPerformanceFilterRoleEnum.LicensedStaff },
        { label: TCPerformanceFilterRoleEnum.ClinicalLiaisonDescription, value: TCPerformanceFilterRoleEnum.ClinicalLiaison }
    ];

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
    }, [isFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (tcPerformanceDateFilter !== TransitionalCareDateRangeEnum.Custom || (Boolean(tcPerformanceFromDate) && Boolean(tcPerformanceToDate))) {
            setIsFetched(false);
            setIsMembersLoading(true);
            setTcPerformanceTab(1);
            setTcCompletedWindow(isClinicalLiaison ? 'Within24hr' : 'Seen1To7');
            setTcIncompleteWindow(isClinicalLiaison ? 'NotWithin24hr' : 'NotSeen1To7');
            setFilteredTcSummary([]);
            const changeFilter = async () => {
                let dateFrom = Boolean(tcPerformanceFromDate) && tcPerformanceDateFilter === TransitionalCareDateRangeEnum.Custom ? new Date(tcPerformanceFromDate).toISOString() : '';
                let dateTo = Boolean(tcPerformanceToDate) && tcPerformanceDateFilter === TransitionalCareDateRangeEnum.Custom ? new Date(tcPerformanceToDate).toISOString() : '';
                const data = await fetchTransitionalCarePerformance(tcPerformanceTeam, tcPerformanceFilterRole, tcPerformanceDateFilter, dateFrom, dateTo, tcPerformanceShowInActiveTeams);
                setTcSummary(data);
                setFilteredTcSummary(data?.SeenFirstWindow ?? []);
                setIsMembersLoading(false);
                setIsFetched(true);
            };
            changeFilter();
        }

        if (tcPerformanceDateFilter !== TransitionalCareDateRangeEnum.Custom && Boolean(tcPerformanceFromDate) && Boolean(tcPerformanceToDate)) {
            setTcPerformanceFromDate(null);
            setTcPerformanceToDate(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsFetched, tcPerformanceTeam, tcPerformanceFilterRole, tcPerformanceDateFilter, tcPerformanceFromDate, tcPerformanceToDate, tcPerformanceShowInActiveTeams]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTcPerformanceTab(newValue);
        setFilteredTcSummary([]);
        if (newValue === 1) {
            setTcCompletedWindow(isClinicalLiaison ? 'Within24hr' : 'Seen1To7');
            setFilteredTcSummary(tcSummary?.SeenFirstWindow ?? []);
        } else if (newValue === 2) {
            setTcIncompleteWindow(isClinicalLiaison ? 'NotWithin24hr' : 'NotSeen1To7');
            setFilteredTcSummary(tcSummary?.NotSeenFirstWindow ?? []);
        } else if (newValue === 3) {
            setFilteredTcSummary(tcSummary?.ReadmittedWithin30Days ?? []);
        }
        else {
        }
    };

    useEffect(() => {
        if (tcSummary) {
            const providerAverageList = tcSummary.ProviderFollowUpAverages ?? [];

            const overallAverage = tcSummary?.OverallAverageFollowUpDays ?? 0;

            const xAxisValues: string[] = [];
            const phValues: any[] = [];
            const bhValues: any[] = [];

            providerAverageList.forEach((dataInfo) => {
                const phValue = dataInfo.AveragePhFollowUpDays ?? 0;
                const bhValue = dataInfo.AverageBhFollowUpDays ?? 0;

                // Skip provider if both PH and BH are zero
                if (phValue === 0 && bhValue === 0) {
                    return;
                }

                xAxisValues.push(dataInfo.ProviderName);

                phValues.push({
                    y: phValue > 0 ? phValue : null,
                    completedCount: dataInfo.PhCompletedCount ?? 0,
                });

                bhValues.push({
                    y: bhValue > 0 ? bhValue : null,
                    completedCount: dataInfo.BhCompletedCount ?? 0,
                });
            });

            const yAxisValues = [
                {
                    name: "PH",
                    data: phValues,
                    color: "#6495ED",
                    showInLegend: true,
                },
                {
                    name: "BH",
                    data: bhValues,
                    color: "#b3b0de",
                    showInLegend: true,
                },
            ];

            if (xAxisValues.length === 0) {
                setChartOptions(
                    configureColumnComparisonChart(
                        "Average TC Completion Days by Provider",
                        "",
                        "",
                        "Average Days",
                        [
                            {
                                name: "",
                                data: [],
                                showInLegend: false,
                                title: { text: null },
                            },
                        ],
                        [0],
                        null,
                        chartHeight,
                        overallAverage
                    )
                );
            } else {
                const options = configureColumnComparisonChart(
                    "Average TC Completion Days by Provider",
                    "",
                    "",
                    "Average Days",
                    yAxisValues,
                    xAxisValues,
                    null,
                    chartHeight,
                    overallAverage
                );

                setChartOptions(options);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tcSummary]);

    const columns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Member Name',
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
                return <>{params.row.LastName} {params.row.FirstName}</>;
            },
        },
        {
            field: 'AdmissionTypeDisplay',
            headerName: 'BH or PH admission',
            minWidth: 200,
        },
        {
            field: 'AdmitDate',
            headerName: 'Admit Date',
            minWidth: 150,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.AdmitDate) ? '-' : dateFormatter(new Date(row.AdmitDate))}`,
        },
        {
            field: 'DischargeDate',
            headerName: 'Discharge Date',
            minWidth: 150,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.DischargeDate) ? '-' : dateFormatter(new Date(row.DischargeDate))}`,
        },
        ...(tcPerformanceTab === 1
            ? [{
                field: 'CompletedByStaff',
                headerName: 'Licensed Staff',
                minWidth: 180,
                flex: 1,
            },
            {
                field: 'FollowUpMode',
                headerName: 'Follow Up Mode',
                minWidth: 180,
                flex: 1,
            },
            {
                field: 'FollowUpVisitDate',
                headerName: 'Follow Up Visit Date',
                minWidth: 150,
                flex: 1,
                valueGetter: (value, row) => `${isDefaultDate(row.FollowUpVisitDate) ? '-' : dateFormatter(new Date(row.FollowUpVisitDate))}`,
            },
            {
                field: 'FollowUpDays',
                headerName: 'Days Between Discharge and Follow-up',
                minWidth: 280,
                flex: 1,
            },
            ]
            : []),
        ...(tcPerformanceTab === 2
            ? [{
                field: 'ResponsibleStaff',
                headerName: 'Responsible Staff',
                minWidth: 180,
                flex: 1,
            },
            {
                field: 'SevenDayPostDischargeDate',
                headerName: '7 Day Post Discharge Date',
                minWidth: 190,
                flex: 1,
                valueGetter: (value, row) => {
                    if (isDefaultDate(row.DischargeDate)) return '-';

                    const date = new Date(row.DischargeDate);
                    date.setDate(date.getDate() + 7);

                    return dateFormatter(date);
                },
            },
            {
                field: 'ThirtyDayPostDischargeDate',
                headerName: '30 Day Post Discharge Date',
                minWidth: 200,
                flex: 1,
                valueGetter: (value, row) => {
                    if (isDefaultDate(row.DischargeDate)) return '-';

                    const date = new Date(row.DischargeDate);
                    date.setDate(date.getDate() + 30);

                    return dateFormatter(date);
                },
            },
            {
                field: 'FollowUpStatus',
                headerName: 'Follow Up Status',
                minWidth: 180,
                flex: 1,
                renderCell: (params) => {
                    const status =
                        params.row.FollowUpStatus === 'NotMetPending'
                            ? 'Not Met - Still Within Time'
                            : params.row.FollowUpStatus === 'NotMetTimedOut'
                                ? 'Not Met - Timed Out'
                                : params.row.FollowUpStatus;

                    return <>{status}</>;
                }
            },
            {
                field: 'DaysPostDischarge',
                headerName: 'Days Post Discharge',
                minWidth: 160,
            },
            ]
            : []),
        ...(tcPerformanceTab === 3
            ? [
                {
                    field: 'ReadmissionDate',
                    headerName: 'Readmission Date',
                    minWidth: 180,
                    flex: 1,
                    valueGetter: (value, row) =>
                        `${isDefaultDate(row.ReadmissionDate) ? '-' : dateFormatter(new Date(row.ReadmissionDate))}`,
                },
            ]
            : []),
    ];

    const exportDetail = async () => {
        setDownLoading(true);
        let start = tcPerformanceFromDate !== null ? new Date(tcPerformanceFromDate).toDateString() : null;
        let end = tcPerformanceToDate !== null ? new Date(tcPerformanceToDate).toDateString() : null;
        const data = await exportTransitionalCarePerformance(tcPerformanceTeam, tcPerformanceFilterRole, tcPerformanceDateFilter, start, end, tcPerformanceShowInActiveTeams);
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `TransitionalCarePerformance - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const getCompletedListByWindow = (data: any, window: string) => {
        switch (window) {
            case 'Seen1To7':
            case 'Within24hr':
                return data?.SeenFirstWindow ?? [];

            case 'Seen8To14':
            case 'Within48hr':
                return data?.SeenSecondWindow ?? [];

            case 'Seen15To30':
            case 'Within72hr':
                return data?.SeenThirdWindow ?? [];

            case 'SeenGreaterThan30':
            case 'WithinGreaterThan72hr':
                return data?.SeenFourthWindow ?? [];

            case 'All':
            default:
                return data?.Completed ?? [];
        }
    };

    const getIncompleteListByWindow = (data: any, window: string) => {
        switch (window) {
            case 'NotSeen1To7':
            case 'NotWithin24hr':
                return data?.NotSeenFirstWindow ?? [];

            case 'NotSeen8To14':
            case 'NotWithin48hr':
                return data?.NotSeenSecondWindow ?? [];

            case 'NotSeen15To30':
            case 'NotWithin72hr':
                return data?.NotSeenThirdWindow ?? [];

            case 'NotSeenGreaterThan30':
            case 'NotSeenGreaterThan72hr':
                return data?.NotSeenFourthWindow ?? [];

            case 'All':
            default:
                return data?.Incomplete ?? [];
        }
    };

    const handleCompletedWindowChange = (
        event: React.MouseEvent<HTMLElement>,
        newValue: string | null
    ) => {
        if (!newValue) return;

        setTcCompletedWindow(newValue);
        setFilteredTcSummary(getCompletedListByWindow(tcSummary, newValue));
    };

    const handleIncompleteWindowChange = (
        event: React.MouseEvent<HTMLElement>,
        newValue: string | null
    ) => {
        if (!newValue) return;

        setTcIncompleteWindow(newValue);
        setFilteredTcSummary(getIncompleteListByWindow(tcSummary, newValue));
    };

    const CustomFooter = () => {
        return (
            <GridFooterContainer>
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 3,
                        px: 2,
                    }}
                >
                    <Typography variant="body2">
                        Total TC Count : #{tcSummary?.All?.length ?? 0}
                    </Typography>

                    <GridPagination />
                </Box>
            </GridFooterContainer>
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Transitional Care Performance
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    {hasPermission && (
                        <Grid size={1.5}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={tcPerformanceTeam}
                                onChange={(id) => setTcPerformanceTeam(id)}
                                showInactive={tcPerformanceShowInActiveTeams}
                                onToggleInactive={setTcPerformanceShowInActiveTeams}
                            />
                        </Grid>
                    )}
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Follow-Up Role</InputLabel>
                            <Select
                                value={tcPerformanceFilterRole}
                                label="Follow-Up Role"
                                name="Follow-Up Role"
                                onChange={(e) =>
                                    setTcPerformanceFilterRole(Number(e.target.value) as TCPerformanceFilterRoleEnum)
                                }
                                size="small"
                                MenuProps={MenuProps}
                                disabled={!hasPermission}
                            >
                                {followUpRules.map((rule) => (
                                    <MenuItem key={rule.value} value={rule.value}>
                                        {rule.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select value={tcPerformanceDateFilter} label="Date Filter" name="Date" onChange={(e) => setTcPerformanceDateFilter(parseInt(e.target.value as any))}>
                                {dateFilterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {tcPerformanceDateFilter === TransitionalCareDateRangeEnum.Custom ? (
                        <React.Fragment>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={tcPerformanceFromDate || null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => setTcPerformanceFromDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                />
                            </Grid>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={Boolean(tcPerformanceToDate) ? tcPerformanceToDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => setTcPerformanceToDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </React.Fragment>
                    ) : null}
                    <Grid size={tcPerformanceDateFilter === TransitionalCareDateRangeEnum.Custom ? 3.2 : 6.2}></Grid>
                    <Grid size={1}>
                        <Button style={{ minWidth: '120px', marginLeft: 'auto' }} onClick={() => exportDetail()} variant="contained" color="success">
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                    <Grid size={12} display={'flex'}>
                        <Tabs value={tcPerformanceTab} onChange={handleAlertsTabChange}>
                            <Tab value={1} label="Completed" />
                            <Tab value={2} label="Incomplete" />
                            <Tab value={3} label="30-Day Readmissions" />
                            <Tab value={4} label="Average" />
                        </Tabs>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2}>
                    <Grid size={12} minHeight="65vh" sx={{ position: 'relative' }}>
                        {isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <>
                                    {
                                        !isFetched ? <WidgetLoading />
                                            : tcSummary?.All?.length > 0 ?
                                                tcPerformanceTab === 4 ?
                                                    <Grid container size={12} spacing={2} paddingLeft={2} paddingRight={2} paddingTop={3}>
                                                        <Typography variant="subtitle1" fontWeight={600} marginBottom={2} paddingLeft={5} sx={{ width: "100%" }}>
                                                            Average Days from Discharge to Follow-Up
                                                        </Typography>
                                                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                                                    </Grid>
                                                    :
                                                    <>
                                                        <Grid container spacing={2} paddingTop={3}>
                                                            {tcPerformanceTab === 1 && (
                                                                <Grid size={12} paddingLeft={2} paddingRight={2}>
                                                                    <ToggleButtonGroup
                                                                        size="small"
                                                                        exclusive
                                                                        value={tcCompletedWindow}
                                                                        onChange={handleCompletedWindowChange}
                                                                    >
                                                                        {completedWindowOptions.map((item) => {
                                                                            const summary = getCompletedSummary(item.window);

                                                                            return (
                                                                                <ToggleButton
                                                                                    key={item.value}
                                                                                    value={item.value}
                                                                                    sx={{ px: 3.7, py: 1.5 }}
                                                                                >
                                                                                    <Box
                                                                                        display="flex"
                                                                                        flexDirection="column"
                                                                                        alignItems="center"
                                                                                        lineHeight={1.2}
                                                                                    >
                                                                                        <span>{item.label}</span>
                                                                                        <span style={{ marginTop: "6px", fontWeight: "bold" }}>
                                                                                            (#{summary?.Count ?? 0} - {summary?.Percentage ?? 0}%)
                                                                                        </span>
                                                                                    </Box>
                                                                                </ToggleButton>
                                                                            );
                                                                        })}
                                                                    </ToggleButtonGroup>
                                                                </Grid>
                                                            )}
                                                            {tcPerformanceTab === 2 && (
                                                                <Grid size={12} paddingLeft={2} paddingRight={2}>
                                                                    <ToggleButtonGroup
                                                                        size="small"
                                                                        exclusive
                                                                        value={tcIncompleteWindow}
                                                                        onChange={handleIncompleteWindowChange}
                                                                    >
                                                                        {incompleteWindowOptions.map((item) => {
                                                                            const summary = getIncompleteSummary(item.window);

                                                                            return (
                                                                                <ToggleButton
                                                                                    key={item.value}
                                                                                    value={item.value}
                                                                                    sx={{ px: 1.7, py: 1.5 }}
                                                                                >
                                                                                    <Box
                                                                                        display="flex"
                                                                                        flexDirection="column"
                                                                                        alignItems="center"
                                                                                        lineHeight={1.2}
                                                                                    >
                                                                                        <span>{item.label}</span>
                                                                                        <span style={{ marginTop: "6px", fontWeight: "bold" }}>
                                                                                            (#{summary?.Count ?? 0} - {summary?.Percentage ?? 0}%)
                                                                                        </span>
                                                                                    </Box>
                                                                                </ToggleButton>
                                                                            );
                                                                        })}
                                                                    </ToggleButtonGroup>
                                                                </Grid>
                                                            )}
                                                            {tcPerformanceTab === 3 && (

                                                                <Grid size={12} paddingLeft={2} paddingRight={2}>
                                                                    <Box
                                                                        display="flex"
                                                                        alignItems="center"
                                                                        gap={4}
                                                                        sx={{
                                                                            padding: "10px 16px",
                                                                            border: "1px solid #ddd",
                                                                            borderRadius: "8px",
                                                                            backgroundColor: "#fafafa",
                                                                            flexWrap: "wrap",
                                                                        }}
                                                                    >
                                                                        <Typography variant="body2" fontWeight={600}>
                                                                            Total TC Count : #{tcSummary?.All?.length ?? 0}
                                                                        </Typography>

                                                                        <Typography variant="body2" fontWeight={600}>
                                                                            Readmission Count : #{tcSummary?.ReadmittedWithin30Days?.length ?? 0}
                                                                        </Typography>

                                                                        <Typography variant="body2" fontWeight={600}>
                                                                            Readmission Percentage : {tcSummary?.ReadmittedWithin30Days?.length && tcSummary?.All?.length ? ((tcSummary.ReadmittedWithin30Days.length / tcSummary.All.length) * 100).toFixed(2) : '0.00'}%
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                        <Grid container spacing={2} marginTop={1} sx={{ height: 300, position: 'relative' }}>
                                                            <Grid size={12} paddingLeft={2} paddingRight={2}>
                                                                <DataGrid
                                                                    key={`${tcCompletedWindow}-${tcIncompleteWindow}-${tcPerformanceTab}`}
                                                                    onCellClick={(params: any) => {
                                                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/transitional-care`, '_blank');
                                                                    }}
                                                                    rows={filteredTcSummary}
                                                                    columns={columns}
                                                                    paginationModel={{ pageSize: pageSize, page: page }}
                                                                    pageSizeOptions={[5]}
                                                                    onPaginationModelChange={(model) => {
                                                                        setPageSize(model.pageSize);
                                                                        setPage(model.page);
                                                                    }}
                                                                    slots={{
                                                                        footer: CustomFooter,
                                                                    }}
                                                                    getRowId={(row) => `${tcCompletedWindow}-${tcIncompleteWindow}-${tcPerformanceTab}-${row.Id}-${row.AdmitDate ?? ''}-${row.DischargeDate ?? ''}-${row.FollowUpStatus ?? ''}`}
                                                                    getRowHeight={() => 'auto'}
                                                                    getRowClassName={(params) => {
                                                                        if (params.row.DisplayColor === 'Green') return 'green';
                                                                        if (params.row.DisplayColor === 'Red') return 'red';
                                                                        if (params.row.DisplayColor === 'Black') return 'black';
                                                                        return '';
                                                                    }}
                                                                    sx={{
                                                                        height: 400,
                                                                        '& .MuiDataGrid-main': {
                                                                            overflow: 'hidden',
                                                                        },
                                                                        '& .MuiDataGrid-virtualScroller': {
                                                                            overflowY: 'auto',
                                                                        },
                                                                        [`& .${gridClasses.cell}`]: {
                                                                            py: 1,
                                                                        },
                                                                        [`& .green`]: {
                                                                            color: 'green',
                                                                        },
                                                                        [`& .red`]: {
                                                                            color: 'red',
                                                                        },
                                                                        [`& .yellow`]: {
                                                                            color: 'gold',
                                                                        },
                                                                        [`& .black`]: {
                                                                            color: 'rgba(0,0,0,0.87)',
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </>
                                                : <Box display="flex" width="100%" justifyContent="center" alignItems="center">
                                                    <Typography variant="h6">No Matching Records</Typography>
                                                </Box>
                                    }
                                </>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default TransitionalCarePerformanceDetail;
